import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  BsCamera,
  BsEye,
  BsPencilSquare,
  BsPlusLg,
  BsSearch,
  BsShieldCheck,
  BsTrash,
  BsX,
} from "react-icons/bs";

import "./dashboardmanagejobs.css";
import Sidebar from "../../components/sidebar/Sidebar";
import {
  serviceGetAllScaleJobs,
  serviceDeleteScaleJobDetails,
} from "../../api/serviceApi";
import { isManager, isDistributor } from "../../auth/auth";
import CameraScanner from "../../components/camera/CameraScanner";
import JobActivateDrawer from "../../components/drawer/JobActivateDrawer";

const SCALE_TYPES = ["Classic", "Compact", "Advanced"];

const COLS = [
  { key: "barcode", label: "Barcode" },
  { key: "status", label: "Status" },
  { key: "scale.scaleType", label: "Scale Type" },
  { key: "scale.iccid", label: "SIM", className: "hide-mobile" },
  { key: "truckDetails.rego", label: "Rego" },
  { key: "truckDetails.make", label: "Make" },
  { key: "truckDetails.model", label: "Model", className: "hide-mobile" },
  { key: "customerDetails.companyName", label: "Company" },
  {
    key: "customerDetails.customerName",
    label: "Customer",
    className: "hide-mobile",
  },
  { key: "addedAt", label: "Added", className: "hide-mobile" },
];

const get = (obj, path, fallback = "") => {
  try {
    return (
      path
        .split(".")
        .reduce(
          (current, key) =>
            current && current[key] != null ? current[key] : undefined,
          obj,
        ) ?? fallback
    );
  } catch {
    return fallback;
  }
};

const fmtDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const getJobId = (job) => job.id || job._id || "";

const DashboardManageJobs = () => {
  const navigate = useNavigate();
  const searchRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [jobActivateOpen, setJobActivateOpen] = useState(false);

  //barcode camera
  const [showScanner, setShowScanner] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadScaleJobs = async () => {
      try {
        setLoading(true);
        setErr("");

        const result = await serviceGetAllScaleJobs();

        if (cancelled) return;

        if (!Array.isArray(result)) {
          throw new Error("Invalid response from server");
        }

        setRows(result);
      } catch (error) {
        if (cancelled) return;

        console.error("Error loading scale jobs:", error);
        setErr(error?.message || "Failed to load scale jobs.");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadScaleJobs();

    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);

    return rows.filter((row) => {
      if (type && get(row, "scale.scaleType") !== type) {
        return false;
      }

      if (!terms.length) {
        return true;
      }

      const haystack = [
        row.barcode,
        row.status,
        get(row, "scale.scaleType"),
        get(row, "scale.serialNumber"),
        get(row, "scale.serverSerialNumber"),
        get(row, "scale.iccid"),
        get(row, "truckDetails.rego"),
        get(row, "truckDetails.make"),
        get(row, "truckDetails.model"),
        get(row, "truckDetails.body"),
        get(row, "truckDetails.vin"),
        get(row, "customerDetails.companyName"),
        get(row, "customerDetails.customerName"),
        get(row, "customerDetails.address"),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return terms.every((term) => haystack.includes(term));
    });
  }, [rows, q, type]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [page, totalPages]);

  const start = (page - 1) * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);

  const handleSearchChange = (event) => {
    setQ(event.target.value);
    setPage(1);
  };

  const handleTypeChange = (event) => {
    setType(event.target.value);
    setPage(1);
  };

  const clearSearch = () => {
    setQ("");
    setPage(1);
    searchRef.current?.focus();
  };

  const onDelete = async (jobId) => {
    const confirmed = window.confirm(
      "Delete this job sheet? This cannot be undone.",
    );

    if (!confirmed) return;

    try {
      await serviceDeleteScaleJobDetails(jobId);

      setRows((previous) => previous.filter((row) => getJobId(row) !== jobId));

      toast.success("Job sheet deleted.");
    } catch (error) {
      console.error("Error deleting scale job:", error);
      toast.error(error?.message || "Failed to delete job sheet.");
    }
  };

  return (
    <div className="manage-jobs-page">
      <Sidebar />

      <main className="manage-jobs-content">
        <div className="manage-jobs-container">
          <header className="manage-jobs-header">
            <div className="manage-jobs-heading">
              <h1>Manage Scale Job Sheets</h1>
              <p>View and manage scale jobs, fittings, parts and repairs.</p>
            </div>

            <div className="manage-jobs-actions">
              <button
                type="button"
                className="mj-btn"
                onClick={() => setShowScanner(true)}
              >
                <BsCamera />
                <span>Scan</span>
              </button>
              {(isManager() || isDistributor()) && (
                <button
                  type="button"
                  className="mj-btn"
                  onClick={() => navigate("/managejobs/add")}
                >
                  <BsPlusLg />
                  <span>Job Sheet</span>
                </button>
              )}
              {(isManager() || isDistributor()) && (
                <button
                  type="button"
                  className="mj-btn primary"
                  onClick={() => setJobActivateOpen(true)}
                >
                  <BsShieldCheck />
                  <span>Activate</span>
                </button>
              )}
            </div>
          </header>

          <section className="manage-jobs-filters">
            <div className="mj-search">
              <label htmlFor="job-search">Search</label>

              <div className="mj-search-control">
                <BsSearch className="mj-search-icon" />

                <input
                  ref={searchRef}
                  id="job-search"
                  type="text"
                  placeholder="Barcode, rego, serial, company..."
                  value={q}
                  onChange={handleSearchChange}
                />

                {q && (
                  <button
                    type="button"
                    className="mj-clear"
                    onClick={clearSearch}
                    aria-label="Clear search"
                    title="Clear search"
                  >
                    <BsX />
                  </button>
                )}
              </div>
            </div>

            <div className="mj-filter-field">
              <label htmlFor="scale-type">Scale Type</label>

              <select id="scale-type" value={type} onChange={handleTypeChange}>
                <option value="">All Types</option>

                {SCALE_TYPES.map((scaleType) => (
                  <option key={scaleType} value={scaleType}>
                    {scaleType}
                  </option>
                ))}
              </select>
            </div>

            <div className="mj-filter-field">
              <label htmlFor="page-size">Rows</label>

              <select
                id="page-size"
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </section>

          {loading ? (
            <div className="manage-jobs-state">
              <div className="mj-spinner" />
              <span>Loading scale jobs...</span>
            </div>
          ) : err ? (
            <div className="manage-jobs-state error">
              <strong>Unable to load jobs</strong>
              <span>{err}</span>
            </div>
          ) : (
            <section className="manage-jobs-table-card">
              <div className="manage-jobs-meta">
                <span>
                  Showing <strong>{pageRows.length}</strong> of{" "}
                  <strong>{filtered.length}</strong> matches
                </span>

                {q && <span className="mj-searching">Searching for “{q}”</span>}
              </div>

              <div className="manage-jobs-table-scroll">
                <table className="manage-jobs-table">
                  <thead>
                    <tr>
                      {COLS.map((column) => (
                        <th key={column.key} className={column.className || ""}>
                          {column.label}
                        </th>
                      ))}

                      <th className="mj-actions-heading">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {pageRows.length === 0 ? (
                      <tr>
                        <td colSpan={COLS.length + 1}>
                          <div className="mj-empty">
                            <BsSearch />

                            <strong>No scale jobs found</strong>

                            <span>
                              Try changing your search or filter options.
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      pageRows.map((row) => {
                        const jobId = getJobId(row);

                        return (
                          <tr key={jobId}>
                            <td>
                              <Link
                                className="mj-barcode"
                                to={`/managejobs/view/${jobId}`}
                              >
                                {row.barcode || "—"}
                              </Link>
                            </td>

                            <td>
                              <span
                                className={`mj-status status-${row.status || ""}`}
                              >
                                {row.status || "—"}
                              </span>
                            </td>

                            <td>{get(row, "scale.scaleType", "—")}</td>

                            <td className="hide-mobile">
                              {get(row, "scale.iccid", "—")}
                            </td>

                            <td>{get(row, "truckDetails.rego", "—")}</td>

                            <td>{get(row, "truckDetails.make", "—")}</td>

                            <td className="hide-mobile">
                              {get(row, "truckDetails.model", "—")}
                            </td>

                            <td>
                              {get(row, "customerDetails.companyName", "—")}
                            </td>

                            <td className="hide-mobile">
                              {get(row, "customerDetails.customerName", "—")}
                            </td>

                            <td className="hide-mobile">
                              {fmtDate(row.addedAt)}
                            </td>

                            <td className="mj-table-actions">
                              <Link
                                className="mj-table-action view"
                                to={`/managejobs/view/${jobId}`}
                                aria-label="View job"
                                title="View job"
                              >
                                <BsEye />
                              </Link>

                              {(isManager() || isDistributor()) && (
                                <>
                                  <Link
                                    className="mj-table-action edit"
                                    to={`/managejobs/edit/${jobId}`}
                                    aria-label="Edit job"
                                    title="Edit job"
                                  >
                                    <BsPencilSquare />
                                  </Link>

                                  <button
                                    type="button"
                                    className="mj-table-action delete"
                                    onClick={() => onDelete(jobId)}
                                    aria-label="Delete job"
                                    title="Delete job"
                                  >
                                    <BsTrash />
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="manage-jobs-pagination">
                  <div className="mj-pagination-info">
                    Page <strong>{page}</strong> of{" "}
                    <strong>{totalPages}</strong>
                  </div>

                  <div className="mj-pagination-buttons">
                    <button
                      type="button"
                      className="mj-btn ghost"
                      disabled={page === 1}
                      onClick={() =>
                        setPage((current) => Math.max(1, current - 1))
                      }
                    >
                      Prev
                    </button>

                    <button
                      type="button"
                      className="mj-btn ghost"
                      disabled={page === totalPages}
                      onClick={() =>
                        setPage((current) => Math.min(totalPages, current + 1))
                      }
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      </main>

      <JobActivateDrawer
        open={jobActivateOpen}
        onclose={() => setJobActivateOpen(false)}
      />

      <CameraScanner
        showScanner={showScanner}
        onClose={() => setShowScanner(false)}
      />
    </div>
  );
};

export default DashboardManageJobs;
